export type QuartzScheduleKind =
  | 'every15Minutes'
  | 'every30Minutes'
  | 'hourly'
  | 'everyHours'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'monthly'
  | 'custom';

export type QuartzWeekday = 'SUN' | 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT';

export interface QuartzScheduleDraft {
  kind: QuartzScheduleKind;
  hour: number;
  minute: number;
  everyHours: number;
  weekday: QuartzWeekday;
  monthDay: number;
  customExpression: string;
}

export const QUARTZ_WEEKDAYS: readonly { id: QuartzWeekday; label: string }[] = [
  { id: 'MON', label: 'Monday' },
  { id: 'TUE', label: 'Tuesday' },
  { id: 'WED', label: 'Wednesday' },
  { id: 'THU', label: 'Thursday' },
  { id: 'FRI', label: 'Friday' },
  { id: 'SAT', label: 'Saturday' },
  { id: 'SUN', label: 'Sunday' },
];

export const QUARTZ_SCHEDULE_KINDS: readonly { id: QuartzScheduleKind; label: string }[] = [
  { id: 'every15Minutes', label: 'Every 15 minutes' },
  { id: 'every30Minutes', label: 'Every 30 minutes' },
  { id: 'hourly', label: 'Every hour' },
  { id: 'everyHours', label: 'Every few hours' },
  { id: 'daily', label: 'Every day' },
  { id: 'weekdays', label: 'Weekdays' },
  { id: 'weekly', label: 'Once a week' },
  { id: 'monthly', label: 'Once a month' },
  { id: 'custom', label: 'Other schedule' },
];

const QUARTZ_FIELDS = /^(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)(?:\s+\S+)?$/;
const WEEKDAY = /^(SUN|MON|TUE|WED|THU|FRI|SAT)$/i;
const WEEKDAY_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

export function emptyQuartzScheduleDraft(expression = '0 0 6 * * ?'): QuartzScheduleDraft {
  return {
    kind: 'daily',
    hour: 6,
    minute: 0,
    everyHours: 2,
    weekday: 'MON',
    monthDay: 1,
    customExpression: expression,
  };
}

export function parseQuartzCron(expression: string): QuartzScheduleDraft {
  const trimmed = expression.trim();
  const fallback = { ...emptyQuartzScheduleDraft(trimmed), kind: 'custom' as const, customExpression: trimmed };
  const match = trimmed.match(QUARTZ_FIELDS);
  if (!match) {
    return fallback;
  }

  const [, seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = match;
  if (seconds !== '0' || trimmed.split(/\s+/).length !== 6) {
    return fallback;
  }

  if (isMinuteInterval(minutes, 15, hours, dayOfMonth, month, dayOfWeek)) {
    return { ...fallback, kind: 'every15Minutes' };
  }
  if (isMinuteInterval(minutes, 30, hours, dayOfMonth, month, dayOfWeek)) {
    return { ...fallback, kind: 'every30Minutes' };
  }

  const minute = Number(minutes);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    return fallback;
  }

  if (isEveryHour(minutes, hours, dayOfMonth, month, dayOfWeek)) {
    return { ...fallback, kind: 'hourly', minute };
  }

  const interval = hours.match(/^(?:0|\*)\/(\d+)$/);
  if (minutes === '0' && interval && isAnyDay(dayOfMonth, month, dayOfWeek)) {
    const everyHours = Number(interval[1]);
    if (everyHours >= 2 && everyHours <= 12) {
      return { ...fallback, kind: 'everyHours', everyHours };
    }
  }

  const hour = Number(hours);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return fallback;
  }

  if (isAnyDay(dayOfMonth, month, dayOfWeek)) {
    return { ...fallback, kind: 'daily', hour, minute };
  }
  if (dayOfMonth === '?' && month === '*' && /^MON-FRI$/i.test(dayOfWeek)) {
    return { ...fallback, kind: 'weekdays', hour, minute };
  }
  if (dayOfMonth === '?' && month === '*' && WEEKDAY.test(dayOfWeek)) {
    return { ...fallback, kind: 'weekly', hour, minute, weekday: dayOfWeek.toUpperCase() as QuartzWeekday };
  }
  if (month === '*' && (dayOfWeek === '?' || dayOfWeek === '*') && /^\d+$/.test(dayOfMonth)) {
    const monthDay = Number(dayOfMonth);
    if (monthDay >= 1 && monthDay <= 31) {
      return { ...fallback, kind: 'monthly', hour, minute, monthDay };
    }
  }

  return fallback;
}

export function buildQuartzCron(draft: QuartzScheduleDraft): string {
  switch (draft.kind) {
    case 'every15Minutes':
      return '0 0/15 * * * ?';
    case 'every30Minutes':
      return '0 0/30 * * * ?';
    case 'hourly':
      return `0 ${draft.minute} * * * ?`;
    case 'everyHours':
      return `0 0 0/${draft.everyHours} * * ?`;
    case 'daily':
      return `0 ${draft.minute} ${draft.hour} * * ?`;
    case 'weekdays':
      return `0 ${draft.minute} ${draft.hour} ? * MON-FRI`;
    case 'weekly':
      return `0 ${draft.minute} ${draft.hour} ? * ${draft.weekday}`;
    case 'monthly':
      return `0 ${draft.minute} ${draft.hour} ${draft.monthDay} * ?`;
    case 'custom':
      return draft.customExpression.trim();
  }
}

export function formatTimeOfDay(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const CUSTOM_MINUTE_INTERVAL = /^0 (?:0|\*)\/(\d+) (?:\*|0\/1|\*\/1) \* \* [?*]$/;

export function formatQuartzCronLabel(
  expression: string,
  translate: (key: string, params?: Record<string, string | number>) => string,
): string {
  const draft = parseQuartzCron(expression);
  const time = formatTimeOfDay(draft.hour, draft.minute);
  switch (draft.kind) {
    case 'every15Minutes':
      return translate('Every 15 minutes');
    case 'every30Minutes':
      return translate('Every 30 minutes');
    case 'hourly':
      return draft.minute === 0
        ? translate('Every hour')
        : translate('QUARTZ_HOURLY_OFFSET_SUMMARY', { minutes: draft.minute });
    case 'everyHours':
      return translate('Quartz schedule every hours', { hours: draft.everyHours });
    case 'daily':
      return translate('Quartz schedule daily', { time });
    case 'weekdays':
      return translate('Quartz schedule weekdays', { time });
    case 'weekly':
      return translate('Quartz schedule weekly', {
        weekday: translate(weekdayLabel(draft.weekday)),
        time,
      });
    case 'monthly':
      return translate('Quartz schedule monthly', { day: draft.monthDay, time });
    case 'custom': {
      const listed = parseDailyHourList(expression);
      if (listed) {
        const times = listed.hours.map(hour => formatTimeOfDay(hour, listed.minute));
        return translate('Quartz schedule daily at times', {
          times: joinScheduleTimes(times, translate),
        });
      }
      const minutes = parseCustomMinuteInterval(expression);
      if (minutes === 1) {
        return translate('Every minute');
      }
      if (minutes != null) {
        return translate('Quartz schedule every minutes', { minutes });
      }
      return draft.customExpression || translate('Other schedule');
    }
  }
}

export function nextQuartzOccurrence(
  expression: string,
  timeZoneId: string,
  from = new Date(),
): Date | null {
  const zone = resolveTimeZone(timeZoneId);
  try {
    return nextExpandableQuartzOccurrence(expression, zone, from);
  } catch {
    return null;
  }
}

const LIST_OCCURRENCE_CAP = 2000;

/**
 * Cron fire instants in `[from, to)`. Known Quartz presets plus `0 0/N * * * ?` (N 1–30).
 */
export function listQuartzOccurrences(
  expression: string,
  timeZoneId: string,
  from: Date,
  to: Date,
  maxCount = LIST_OCCURRENCE_CAP,
): Date[] {
  if (!(from instanceof Date) || !(to instanceof Date) || to.getTime() <= from.getTime()) {
    return [];
  }
  const zone = resolveTimeZone(timeZoneId);
  const cap = Math.min(Math.max(1, maxCount), LIST_OCCURRENCE_CAP);
  const dates: Date[] = [];
  let cursor = from;
  try {
    for (let index = 0; index < cap; index += 1) {
      const next = nextExpandableQuartzOccurrence(expression, zone, cursor);
      if (!next || next.getTime() >= to.getTime()) {
        break;
      }
      if (next.getTime() <= cursor.getTime()) {
        break;
      }
      dates.push(next);
      cursor = next;
    }
  } catch {
    return dates;
  }
  return dates;
}

function nextExpandableQuartzOccurrence(
  expression: string,
  zone: string,
  from: Date,
): Date | null {
  const draft = parseQuartzCron(expression);
  if (draft.kind !== 'custom') {
    switch (draft.kind) {
      case 'every15Minutes':
        return nextMinuteInterval(from, zone, 15);
      case 'every30Minutes':
        return nextMinuteInterval(from, zone, 30);
      case 'hourly':
        return nextHourly(from, zone, draft.minute);
      case 'everyHours':
        return nextHourInterval(from, zone, draft.everyHours);
      case 'daily':
        return nextMatchingDay(from, zone, draft.hour, draft.minute, () => true);
      case 'weekdays':
        return nextMatchingDay(from, zone, draft.hour, draft.minute, local => local.weekday >= 1 && local.weekday <= 5);
      case 'weekly':
        return nextMatchingDay(from, zone, draft.hour, draft.minute, local => local.weekday === WEEKDAY_INDEX[draft.weekday]);
      case 'monthly':
        return nextMatchingDay(from, zone, draft.hour, draft.minute, local => local.day === draft.monthDay);
    }
  }

  const listed = parseDailyHourList(expression);
  if (listed) {
    return nextDailyAtHours(from, zone, listed.hours, listed.minute);
  }

  const step = parseCustomMinuteInterval(expression);
  if (step == null) {
    return null;
  }
  return nextMinuteInterval(from, zone, step);
}

function parseCustomMinuteInterval(expression: string): number | null {
  const minute = expression.trim().match(CUSTOM_MINUTE_INTERVAL);
  if (!minute) {
    return null;
  }
  const step = Number(minute[1]);
  if (!Number.isInteger(step) || step < 1 || step > 30) {
    return null;
  }
  return step;
}

function parseDailyHourList(expression: string): { hours: number[]; minute: number } | null {
  const match = expression.trim().match(QUARTZ_FIELDS);
  if (!match) {
    return null;
  }
  const [, seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = match;
  if (seconds !== '0' || !isAnyDay(dayOfMonth, month, dayOfWeek)) {
    return null;
  }
  const minute = Number(minutes);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    return null;
  }
  const hourList = parseHourList(hours);
  if (!hourList) {
    return null;
  }
  return { hours: hourList, minute };
}

function parseHourList(hours: string): number[] | null {
  const parts = hours.split(',');
  if (parts.length < 2) {
    return null;
  }
  const values: number[] = [];
  const seen = new Set<number>();
  for (const part of parts) {
    if (!/^\d{1,2}$/.test(part)) {
      return null;
    }
    const hour = Number(part);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23 || seen.has(hour)) {
      return null;
    }
    seen.add(hour);
    values.push(hour);
  }
  return values.sort((left, right) => left - right);
}

function joinScheduleTimes(
  times: string[],
  translate: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (times.length <= 1) {
    return times[0] ?? '';
  }
  const and = translate('Quartz schedule time list and');
  if (times.length === 2) {
    return `${times[0]} ${and} ${times[1]}`;
  }
  return `${times.slice(0, -1).join(', ')} ${and} ${times[times.length - 1]}`;
}

function weekdayLabel(weekday: QuartzWeekday): string {
  return QUARTZ_WEEKDAYS.find(item => item.id === weekday)?.label ?? 'Monday';
}

function isAnyDay(dayOfMonth: string, month: string, dayOfWeek: string): boolean {
  return dayOfMonth === '*' && month === '*' && (dayOfWeek === '?' || dayOfWeek === '*');
}

function isEveryHour(
  minutes: string,
  hours: string,
  dayOfMonth: string,
  month: string,
  dayOfWeek: string,
): boolean {
  return /^\d+$/.test(minutes) && Number(minutes) <= 59
    && (hours === '*' || hours === '0/1' || hours === '*/1')
    && isAnyDay(dayOfMonth, month, dayOfWeek);
}

function isMinuteInterval(
  minutes: string,
  interval: number,
  hours: string,
  dayOfMonth: string,
  month: string,
  dayOfWeek: string,
): boolean {
  return (minutes === `0/${interval}` || minutes === `*/${interval}`)
    && (hours === '*' || hours === '0/1' || hours === '*/1')
    && isAnyDay(dayOfMonth, month, dayOfWeek);
}

function resolveTimeZone(timeZoneId: string): string {
  const trimmed = timeZoneId.trim() || 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: trimmed }).format(new Date());
    return trimmed;
  } catch {
    return 'UTC';
  }
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const map = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
  const weekdayKey = (map['weekday'] ?? 'Sun').slice(0, 3).toUpperCase();
  return {
    year: Number(map['year']),
    month: Number(map['month']),
    day: Number(map['day']),
    hour: Number(map['hour']),
    minute: Number(map['minute']),
    second: Number(map['second']),
    weekday: WEEKDAY_INDEX[weekdayKey] ?? 0,
  };
}

function zonedDate(
  timeZone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const first = new Date(utcGuess - zonedOffsetMs(new Date(utcGuess), timeZone));
  return new Date(utcGuess - zonedOffsetMs(first, timeZone));
}

function zonedOffsetMs(date: Date, timeZone: string): number {
  const parts = zonedParts(date, timeZone);
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - date.getTime();
}

function addCalendarDays(year: number, month: number, day: number, days: number): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function nextMinuteInterval(from: Date, timeZone: string, interval: number): Date {
  const local = zonedParts(from, timeZone);
  let total = local.hour * 60 + local.minute;
  if (local.second > 0 || local.minute % interval !== 0) {
    total = Math.floor(total / interval) * interval + interval;
  } else {
    total += interval;
  }
  const dayOffset = Math.floor(total / (24 * 60));
  total -= dayOffset * 24 * 60;
  const date = addCalendarDays(local.year, local.month, local.day, dayOffset);
  return zonedDate(timeZone, date.year, date.month, date.day, Math.floor(total / 60), total % 60);
}

function nextHourly(from: Date, timeZone: string, minute: number): Date | null {
  // Scan real instants so repeated or missing local hours follow time-zone transitions.
  const minuteFormatter = new Intl.DateTimeFormat('en-US', { timeZone, minute: 'numeric' });
  const firstMinute = Math.floor(from.getTime() / 60000) * 60000 + 60000;
  for (let offset = 0; offset < 26 * 60; offset += 1) {
    const candidate = new Date(firstMinute + offset * 60000);
    if (Number(minuteFormatter.format(candidate)) === minute) {
      return candidate;
    }
  }
  return null;
}

function nextHourInterval(from: Date, timeZone: string, interval: number): Date {
  const local = zonedParts(from, timeZone);
  let hour = local.hour;
  let dayOffset = 0;
  const onSlot = hour % interval === 0 && local.minute === 0 && local.second === 0;
  if (onSlot || local.minute > 0 || local.second > 0 || hour % interval !== 0) {
    hour += 1;
    while (hour % interval !== 0) {
      hour += 1;
    }
  }
  if (hour >= 24) {
    dayOffset = Math.floor(hour / 24);
    hour %= 24;
    while (hour % interval !== 0) {
      hour += 1;
      if (hour >= 24) {
        dayOffset += 1;
        hour = 0;
      }
    }
  }
  const date = addCalendarDays(local.year, local.month, local.day, dayOffset);
  return zonedDate(timeZone, date.year, date.month, date.day, hour, 0);
}

function nextDailyAtHours(from: Date, timeZone: string, hours: number[], minute: number): Date | null {
  const start = zonedParts(from, timeZone);
  for (let offset = 0; offset < 400; offset += 1) {
    const date = addCalendarDays(start.year, start.month, start.day, offset);
    for (const hour of hours) {
      const candidate = zonedDate(timeZone, date.year, date.month, date.day, hour, minute);
      const local = zonedParts(candidate, timeZone);
      if (local.hour !== hour || local.minute !== minute) {
        continue;
      }
      if (candidate.getTime() > from.getTime()) {
        return candidate;
      }
    }
  }
  return null;
}

function nextMatchingDay(
  from: Date,
  timeZone: string,
  hour: number,
  minute: number,
  matches: (local: ZonedParts) => boolean,
): Date | null {
  const start = zonedParts(from, timeZone);
  for (let offset = 0; offset < 400; offset += 1) {
    const date = addCalendarDays(start.year, start.month, start.day, offset);
    const candidate = zonedDate(timeZone, date.year, date.month, date.day, hour, minute);
    const local = zonedParts(candidate, timeZone);
    if (local.hour !== hour || local.minute !== minute) {
      continue;
    }
    if (matches(local) && candidate.getTime() > from.getTime()) {
      return candidate;
    }
  }
  return null;
}
